/**
 * result-v2/tokens.ts
 * ---------------------------------------------------------------------------
 * Design tokens for the redesigned result views, taken from espysys.com so the
 * portal and the marketing site read as one product.
 *
 * Deliberately plain objects rather than a global MUI theme override, so this
 * folder can be dropped in without touching src/mui.ts. When you are ready to
 * adopt them globally, move `light` / `dark` into createTheme().
 */

export const brand = {
  primary: '#2D89F4',
  primaryDark: '#1B6FD6',
  primaryDarker: '#1559B0',
  ink: '#0B081D',
  navy: '#181542',
  ok: '#10B981',
  warn: '#F59E0B',
  danger: '#EF4444',
  violet: '#7C5CFF',
}

export const light = {
  bg: '#F4F7FC',
  surface: '#FFFFFF',
  surface2: '#F8FAFF',
  surface3: '#EFF4FC',
  border: '#E2E8F0',
  borderStrong: '#CBD5E1',
  text: '#0F172A',
  text2: '#64748B',
  text3: '#94A3B8',
  brand050: '#EEF5FF',
  brand100: '#DBE9FE',
}

export const dark: typeof light = {
  bg: '#08071A',
  surface: '#12112C',
  surface2: '#171634',
  surface3: '#1E1D42',
  border: '#272654',
  borderStrong: '#3A3872',
  text: '#E9EDF9',
  text2: '#98A2C0',
  text3: '#6F779A',
  brand050: 'rgba(45,137,244,.14)',
  brand100: 'rgba(45,137,244,.24)',
}

export type Tokens = typeof light

/** Resolve tokens for the active MUI palette mode. */
export const tokensFor = (mode: 'light' | 'dark' | string): Tokens =>
  mode === 'dark' ? dark : light

export const radii = { sm: 8, md: 11, card: 15, header: 18, xl: 22 }

/** Provider brand colours, used for the small source badges. */
export const SOURCE_COLOR: Record<string, string> = {
  tc: '#EC4899',
  truecaller: '#EC4899',
  viber: '#7C5CFF',
  whatsapp: '#25D366',
  telegram: '#229ED9',
  getcontact: '#F59E0B',
  luna: '#0EA5A5',
  eyecon: '#64748B',
  hiya: '#64748B',
  callapp: '#64748B',
  botim: '#64748B',
  imo: '#64748B',
  viewcaller: '#0077FF',
  teams: '#6264A7',
  skype: '#00AFF0',
  glook: '#111827',
  hbp: '#EF4444',
  mail_reveal: '#0A66C2',
  linkedin: '#0A66C2',
  smf: '#334155',
  web_collection: '#334155',
  web_collection_phone: '#334155',
  verifier: '#10B981',
  sniper: '#0EA5A5',
  photo_intel: '#EC4899',
  psychanalyst: '#EC4899',
  pluto: '#EF4444',
  hydra: '#EF4444',
  // Snapchat's real brand yellow (#FFFC00) is unreadable as the white-on-color
  // badge text every other network badge uses here — this is a darkened
  // stand-in chosen for contrast, not the literal brand colour.
  snapchat: '#CC9A00',
}

/** Social network colours, used by profile lists and match cards. */
export const NETWORK: Record<string, { label: string; color: string }> = {
  FB: { label: 'Facebook', color: '#1877F2' },
  FACEBOOK: { label: 'Facebook', color: '#1877F2' },
  INSTAGRAM: { label: 'Instagram', color: '#E4405F' },
  LINKEDIN: { label: 'LinkedIn', color: '#0A66C2' },
  TWITTER: { label: 'X / Twitter', color: '#111827' },
  X: { label: 'X / Twitter', color: '#111827' },
  OK: { label: 'OK.ru', color: '#EE8208' },
  TIKTOK: { label: 'TikTok', color: '#000000' },
  VK: { label: 'VK', color: '#0077FF' },
  BLUESKY: { label: 'Bluesky', color: '#0085FF' },
  PLUTO: { label: 'Leak records', color: '#EF4444' },
  SKYPE: { label: 'Skype', color: '#00AFF0' },
  TELEGRAM: { label: 'Telegram', color: '#229ED9' },
  // Same contrast note as SOURCE_COLOR.snapchat above — darkened stand-in,
  // not the literal #FFFC00 brand yellow.
  SNAPCHAT: { label: 'Snapchat', color: '#CC9A00' },
}

export const networkMeta = (n?: string) =>
  NETWORK[String(n || '').toUpperCase()] || { label: n || 'Unknown', color: '#64748B' }

export const sourceColor = (s?: string) =>
  SOURCE_COLOR[String(s || '').toLowerCase()] || '#64748B'

/** Two-letter badge for a provider or network key. */
export const initials = (s?: string) =>
  String(s || '?')
    .replace(/[^A-Za-z0-9]/g, '')
    .slice(0, 2)
    .toUpperCase() || '?'

/**
 * The app theme forces `display: inline` on MuiTypography body1/body2
 * (src/mui.ts). Spread this into the `sx` of a feature root to restore block
 * flow, while leaving intentional `component="span"` labels inline.
 */
export const TYPO_BLOCK_FIX = {
  '& .MuiTypography-root:not(span)': { display: 'block' },
} as const
